// frontend/src/pages/About.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSeedling,
  faUsers,
  faTractor,
  faLeaf,
  faHandshake,
  faCheckCircle,
  faTruck,
  faGlobeAfrica,
  faStar,
  faQuoteLeft,
  faAward,
  faRocket,
  faHeart,
  faBullseye,
  faBuilding,
  faArrowRight,
  faSpinner,
  faUserCircle,
  faEnvelope,
  faPhone,
  faExclamationTriangle,
  faGlobe,
  faShip,
  faWarehouse,
  faClock,
  faCalendarCheck,
  faWhatsapp,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import Button from "../components/UI/Button";
import { getUsers } from "../api";
import { useAuth } from "../context/AuthContext";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// SUPPLIER CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// DATA
// ============================================
const VALUES = [
  {
    icon: faSeedling,
    title: "Quality First",
    description:
      "We source only the highest quality ingredients from trusted manufacturers in Malaysia and India.",
  },
  {
    icon: faHandshake,
    title: "Farmer-Centric",
    description:
      "Every product is designed with the farmer's success and profitability in mind.",
  },
  {
    icon: faLeaf,
    title: "Sustainable Nutrition",
    description:
      "Committed to sustainable livestock nutrition solutions for African agriculture.",
  },
  {
    icon: faGlobeAfrica,
    title: "African Growth",
    description:
      "Dedicated to strengthening agricultural value chains across Sierra Leone and West Africa.",
  },
];

const MILESTONES = [
  {
    year: "2024",
    title: "Company Founded",
    description:
      "Kenali Enterprise established to serve Sierra Leone's livestock sector.",
  },
  {
    year: "2024",
    title: "First Shipment",
    description: `Initial product shipment from ${SUPPLIER.country} to Sierra Leone.`,
  },
  {
    year: "2025",
    title: "Product Launch",
    description: "Official launch of GrowPro™, DairyBoost™, and Mineralite™.",
  },
  {
    year: "2025",
    title: "Partnerships",
    description:
      `Strategic partnerships with ${SUPPLIER.name} and other leading manufacturers established.`,
  },
];

const FALLBACK_TEAM = [
  {
    id: "1",
    name: "Bojurie Rogers-Wright",
    role: "Co-Founder",
    bio: "Passionate about agricultural development and livestock nutrition in Sierra Leone.",
    avatar: null,
    email: "bojurie@kenali.com",
    phone: "",
  },
  {
    id: "2",
    name: "Idris Rogers-Wright",
    role: "Co-Founder",
    bio: "Dedicated to building sustainable supply chains for African farmers.",
    avatar: null,
    email: "idris@kenali.com",
    phone: "",
  },
  {
    id: "3",
    name: "Kemen Rogers-Wright",
    role: "Co-Founder",
    bio: "Focused on product quality and customer satisfaction.",
    avatar: null,
    email: "kemen@kenali.com",
    phone: "",
  },
  {
    id: "4",
    name: "Nala Rogers-Wright",
    role: "Co-Founder",
    bio: "Committed to innovation in livestock nutrition solutions.",
    avatar: null,
    email: "nala@kenali.com",
    phone: "",
  },
];

// ============================================
// MAIN COMPONENT
// ============================================
const About = () => {
  const { user, isAuthenticated } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // ============================================
  // FETCH TEAM
  // ============================================
  useEffect(() => {
    const fetchTeamMembers = async () => {
      setLoading(true);
      setError(null);
      setUsingFallback(false);

      if (!isAuthenticated || user?.role !== "admin") {
        setTeamMembers(FALLBACK_TEAM);
        setUsingFallback(true);
        setLoading(false);
        return;
      }

      try {
        const response = await getUsers({
          role: "admin",
          limit: 20,
        });
        const users = response.data?.users || response.data || [];

        const team = users
          .filter((u) => u.role === "admin" || u.isTeamMember === true)
          .map((u) => ({
            id: u._id,
            name: u.name || "Team Member",
            role: u.teamRole || u.role || "Team Member",
            bio: u.bio || "Dedicated to serving our farmers and customers.",
            avatar: u.avatar || null,
            email: u.email || "",
            phone: u.phone || "",
          }));

        if (team.length === 0) {
          setTeamMembers(FALLBACK_TEAM);
          setUsingFallback(true);
        } else {
          setTeamMembers(team);
          setUsingFallback(false);
        }
      } catch (err) {
        console.error("Error fetching team members:", err);
        setTeamMembers(FALLBACK_TEAM);
        setUsingFallback(true);
        if (err.response?.status === 401) {
          setError(
            "Team member data requires admin access. Showing default team.",
          );
        } else {
          setError("Could not load team members. Showing default team.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, [isAuthenticated, user]);

  // ============================================
  // HELPERS
  // ============================================
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <motion.div
      className="about-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="about-hero">
        <div className="about-hero__overlay" />
        <div className="container about-hero__content">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FontAwesomeIcon icon={faSeedling} className="about-hero__icon" />
            <h1>About Kenali Enterprise</h1>
            <p>
              Premium livestock nutrition solutions for healthier animals and
              improved farm productivity. Sourced from{" "}
              <strong>{SUPPLIER.name}</strong> in{" "}
              <strong>{SUPPLIER.country}</strong>.
            </p>
            <div className="about-hero__badges">
              <span>
                <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} Origin
              </span>
              <span>
                <FontAwesomeIcon icon={faBuilding} /> {SUPPLIER.name}
              </span>
              <span>
                <FontAwesomeIcon icon={faShip} /> {SUPPLIER.portLoading}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          OUR STORY
          ============================================ */}
      <section className="container about-story">
        <div className="about-story__grid">
          <motion.div
            className="about-story__text"
            initial={{ x: -30, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="section-tag">Our Story</span>
            <h2>Building Africa's Agricultural Future</h2>
            <div className="gold-divider" />
            <p>
              Kenali Enterprise was founded with a mission to bring premium
              livestock nutrition solutions to farmers across Sierra Leone. We
              understand the challenges farmers face in accessing quality feed
              supplements that genuinely improve animal performance and farm
              profitability.
            </p>
            <p>
              Our name, <strong>Kenali</strong>, represents the combination of
              our founding team's vision and commitment to agricultural
              transformation in Africa. We believe in the power of quality
              nutrition to transform livestock production and farmer
              livelihoods.
            </p>
            <p>
              We source our products from trusted manufacturers in{" "}
              <strong>{SUPPLIER.country}</strong> and India, ensuring every
              product meets rigorous quality standards before reaching our
              farmers.
            </p>
            <div className="about-story__actions">
              <Button href="/products" variant="primary">
                Explore Our Products <FontAwesomeIcon icon={faArrowRight} />
              </Button>
              <Button href="/contact" variant="outline">
                Contact Us
              </Button>
            </div>
          </motion.div>

          <motion.div
            className="about-story__mission"
            initial={{ x: 30, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <FontAwesomeIcon icon={faTractor} className="mission-icon" />
            <h3>Our Mission</h3>
            <p>
              To empower African farmers with premium livestock nutrition
              solutions that enhance animal health, productivity, and
              profitability.
            </p>
            <div className="mission-stats">
              <div>
                <div className="stat-number">500+</div>
                <div className="stat-label">Happy Farmers</div>
              </div>
              <div>
                <div className="stat-number">50+</div>
                <div className="stat-label">Tons Delivered</div>
              </div>
              <div>
                <div className="stat-number">100%</div>
                <div className="stat-label">Quality Guarantee</div>
              </div>
            </div>
            <div className="mission-supplier">
              <FontAwesomeIcon icon={faShip} />
              <span>Proudly sourced from {SUPPLIER.name}, {SUPPLIER.country}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          VALUES SECTION
          ============================================ */}
      <section className="about-values">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Values</span>
            <h2>What Drives Kenali Enterprise</h2>
            <p className="section-subtitle">
              The principles that guide our work and relationships
            </p>
          </div>
          <div className="values-grid">
            {VALUES.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
              >
                <div className="value-icon-wrapper">
                  <FontAwesomeIcon icon={value.icon} />
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          MILESTONES
          ============================================ */}
      <section className="about-milestones">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Journey</span>
            <h2>Company Milestones</h2>
            <p className="section-subtitle">
              Key moments in Kenali Enterprise's journey
            </p>
          </div>
          <div className="milestones-grid">
            {MILESTONES.map((milestone, index) => (
              <motion.div
                key={index}
                className="milestone-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="milestone-year">{milestone.year}</div>
                <h4>{milestone.title}</h4>
                <p>{milestone.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          TEAM SECTION
          ============================================ */}
      <section className="about-team">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Team</span>
            <h2>Meet the Founders</h2>
            <p className="section-subtitle">
              The people behind Kenali Enterprise
            </p>
            {usingFallback && !loading && (
              <div className="team-notice">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>
                  {error ||
                    "Showing our founding team. Additional team members available to admins."}
                </span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="team-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading team members...</span>
            </div>
          ) : (
            <div className="team-grid">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={member.id || index}
                  className="team-card"
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ delay: Math.min(index * 0.1, 0.5) }}
                  viewport={{ once: true }}
                  whileHover={{ y: -4 }}
                >
                  <div className="team-avatar">
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.name} />
                    ) : (
                      getInitials(member.name)
                    )}
                  </div>
                  <h3>{member.name}</h3>
                  <div className="team-role">{member.role}</div>
                  <p>{member.bio}</p>
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="team-contact">
                      <FontAwesomeIcon icon={faEnvelope} />
                      {member.email}
                    </a>
                  )}
                  {member.phone && (
                    <a href={`tel:${member.phone}`} className="team-contact">
                      <FontAwesomeIcon icon={faPhone} />
                      {member.phone}
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============================================
          CTA SECTION
          ============================================ */}
      <section className="about-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Partner with Us?</h2>
            <p>
              Let's work together to improve livestock productivity in Sierra
              Leone.
            </p>
            <div className="cta-buttons">
              <Button href="/contact" variant="primary" size="large">
                Contact Us <FontAwesomeIcon icon={faArrowRight} />
              </Button>
              <Button
                href="/products"
                variant="outline"
                size="large"
                className="btn-outline-white"
              >
                Browse Products
              </Button>
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-cta"
              >
                <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        /* ============================================
           ABOUT PAGE STYLES - Mobile-First
           ============================================ */

        .about-page {
          min-height: 100vh;
          background: #ffffff;
        }

        /* ========== HERO ========== */
        .about-hero {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          color: #ffffff;
          padding: 3rem 1.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        @media (min-width: 768px) {
          .about-hero {
            padding: 4rem 2rem;
          }
        }

        .about-hero__overlay {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(217, 163, 33, 0.05);
          border-radius: 50%;
        }

        @media (min-width: 768px) {
          .about-hero__overlay {
            width: 500px;
            height: 500px;
          }
        }

        .about-hero__content {
          position: relative;
          z-index: 2;
        }

        .about-hero__icon {
          font-size: 2rem;
          color: #D9A321;
          margin-bottom: 0.75rem;
        }

        @media (min-width: 768px) {
          .about-hero__icon {
            font-size: 2.5rem;
            margin-bottom: 1rem;
          }
        }

        .about-hero h1 {
          font-size: clamp(1.75rem, 5vw, 2.5rem);
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }

        .about-hero p {
          font-size: 0.938rem;
          max-width: 600px;
          margin: 0 auto;
          opacity: 0.9;
          line-height: 1.7;
        }

        .about-hero p strong {
          color: #D9A321;
        }

        @media (min-width: 768px) {
          .about-hero p {
            font-size: 1.125rem;
          }
        }

        .about-hero__badges {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
          margin-top: 0.75rem;
          font-size: 0.7rem;
          opacity: 0.8;
        }

        .about-hero__badges span {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* ========== CONTAINER ========== */
        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        @media (min-width: 768px) {
          .container {
            padding: 0 2rem;
          }
        }

        /* ========== SECTION TAG ========== */
        .section-tag {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #D9A321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          letter-spacing: 0.5px;
        }

        @media (min-width: 768px) {
          .section-tag {
            margin-bottom: 1rem;
          }
        }

        /* ========== STORY ========== */
        .about-story {
          padding: 3rem 0;
        }

        @media (min-width: 768px) {
          .about-story {
            padding: 4rem 0;
          }
        }

        .about-story__grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: center;
        }

        @media (min-width: 768px) {
          .about-story__grid {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
          }
        }

        @media (min-width: 1024px) {
          .about-story__grid {
            gap: 4rem;
          }
        }

        .about-story__text h2 {
          color: #006B2E;
          margin-bottom: 1rem;
          font-size: clamp(1.5rem, 2.5vw, 2rem);
          font-family: "Montserrat", sans-serif;
        }

        .gold-divider {
          width: 50px;
          height: 3px;
          background: #D9A321;
          margin-bottom: 1.5rem;
          border-radius: 2px;
        }

        .about-story__text p {
          color: #5A5A5A;
          line-height: 1.8;
          margin-bottom: 1rem;
          font-size: 0.938rem;
        }

        @media (min-width: 768px) {
          .about-story__text p {
            font-size: 0.95rem;
          }
        }

        .about-story__actions {
          margin-top: 1.5rem;
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        @media (min-width: 768px) {
          .about-story__actions {
            gap: 1rem;
          }
        }

        /* ========== MISSION ========== */
        .about-story__mission {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          border-radius: 16px;
          padding: 1.5rem;
          text-align: center;
          color: #ffffff;
        }

        @media (min-width: 768px) {
          .about-story__mission {
            padding: 2.5rem;
          }
        }

        .mission-icon {
          font-size: 2.5rem;
          color: #D9A321;
          margin-bottom: 0.75rem;
        }

        @media (min-width: 768px) {
          .mission-icon {
            font-size: 3.5rem;
            margin-bottom: 1rem;
          }
        }

        .about-story__mission h3 {
          margin-bottom: 0.5rem;
          font-size: 1.125rem;
          font-family: "Montserrat", sans-serif;
        }

        @media (min-width: 768px) {
          .about-story__mission h3 {
            font-size: 1.25rem;
          }
        }

        .about-story__mission p {
          opacity: 0.9;
          line-height: 1.6;
          max-width: 350px;
          margin: 0 auto;
          font-size: 0.875rem;
        }

        @media (min-width: 768px) {
          .about-story__mission p {
            font-size: 0.95rem;
          }
        }

        .mission-stats {
          margin-top: 1.5rem;
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        @media (min-width: 768px) {
          .mission-stats {
            gap: 2rem;
          }
        }

        .mission-stats .stat-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: #D9A321;
          font-family: "Montserrat", sans-serif;
        }

        @media (min-width: 768px) {
          .mission-stats .stat-number {
            font-size: 1.75rem;
          }
        }

        .mission-stats .stat-label {
          font-size: 0.7rem;
          opacity: 0.8;
        }

        @media (min-width: 768px) {
          .mission-stats .stat-label {
            font-size: 0.8rem;
          }
        }

        .mission-supplier {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          font-size: 0.7rem;
          opacity: 0.7;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        /* ========== VALUES ========== */
        .about-values {
          background: #F8F9FA;
          padding: 2rem 0;
        }

        @media (min-width: 768px) {
          .about-values {
            padding: 4rem 0;
          }
        }

        .section-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        @media (min-width: 768px) {
          .section-header {
            margin-bottom: 2.5rem;
          }
        }

        .section-header h2 {
          color: #006B2E;
          font-size: clamp(1.5rem, 2.5vw, 2rem);
          font-family: "Montserrat", sans-serif;
        }

        .section-subtitle {
          color: #5A5A5A;
          max-width: 600px;
          margin: 0 auto;
          font-size: 0.875rem;
        }

        @media (min-width: 768px) {
          .section-subtitle {
            font-size: 0.938rem;
          }
        }

        .values-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        @media (min-width: 480px) {
          .values-grid {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
        }

        @media (min-width: 768px) {
          .values-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 1.5rem;
          }
        }

        .value-card {
          background: #ffffff;
          border-radius: 12px;
          padding: 1.25rem;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          border: 1px solid rgba(0, 0, 0, 0.04);
        }

        @media (min-width: 768px) {
          .value-card {
            padding: 1.75rem;
          }
        }

        .value-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
          border-color: #D9A321;
        }

        .value-icon-wrapper {
          width: 48px;
          height: 48px;
          background: rgba(0, 107, 46, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          transition: all 0.3s ease;
        }

        @media (min-width: 768px) {
          .value-icon-wrapper {
            width: 56px;
            height: 56px;
            margin: 0 auto 1rem;
          }
        }

        .value-card:hover .value-icon-wrapper {
          background: rgba(217, 163, 33, 0.15);
        }

        .value-icon-wrapper svg {
          font-size: 1.25rem;
          color: #006B2E;
          transition: color 0.3s ease;
        }

        @media (min-width: 768px) {
          .value-icon-wrapper svg {
            font-size: 1.5rem;
          }
        }

        .value-card:hover .value-icon-wrapper svg {
          color: #D9A321;
        }

        .value-card h3 {
          font-size: 0.938rem;
          margin-bottom: 0.375rem;
          color: #006B2E;
          font-family: "Montserrat", sans-serif;
        }

        @media (min-width: 768px) {
          .value-card h3 {
            font-size: 1rem;
            margin-bottom: 0.5rem;
          }
        }

        .value-card p {
          font-size: 0.813rem;
          color: #5A5A5A;
          line-height: 1.6;
        }

        @media (min-width: 768px) {
          .value-card p {
            font-size: 0.875rem;
          }
        }

        /* ========== MILESTONES ========== */
        .about-milestones {
          padding: 2rem 0;
          background: #ffffff;
        }

        @media (min-width: 768px) {
          .about-milestones {
            padding: 4rem 0;
          }
        }

        .milestones-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        @media (min-width: 480px) {
          .milestones-grid {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
        }

        @media (min-width: 768px) {
          .milestones-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 1.5rem;
          }
        }

        .milestone-card {
          background: #F8F9FA;
          border-radius: 12px;
          padding: 1.25rem;
          text-align: center;
          border: 1px solid #e5e5e5;
          transition: all 0.3s ease;
        }

        @media (min-width: 768px) {
          .milestone-card {
            padding: 1.75rem;
          }
        }

        .milestone-card:hover {
          border-color: #D9A321;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .milestone-year {
          font-size: 1.5rem;
          font-weight: 700;
          color: #D9A321;
          font-family: "Montserrat", sans-serif;
          margin-bottom: 0.25rem;
        }

        .milestone-card h4 {
          font-size: 0.938rem;
          color: #006B2E;
          margin-bottom: 0.25rem;
          font-family: "Montserrat", sans-serif;
        }

        .milestone-card p {
          font-size: 0.813rem;
          color: #5A5A5A;
          line-height: 1.5;
          margin: 0;
        }

        /* ========== TEAM ========== */
        .about-team {
          padding: 2rem 0;
          background: #F8F9FA;
        }

        @media (min-width: 768px) {
          .about-team {
            padding: 4rem 0;
          }
        }

        .team-notice {
          margin-top: 0.75rem;
          padding: 0.625rem 1rem;
          background: rgba(217, 163, 33, 0.08);
          color: #D9A321;
          font-size: 0.813rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          justify-content: center;
          border: 1px solid rgba(217, 163, 33, 0.15);
        }

        .team-notice svg {
          font-size: 0.875rem;
        }

        .team-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 2rem;
          color: #5A5A5A;
          font-size: 0.875rem;
        }

        .team-loading svg {
          font-size: 1.5rem;
          color: #D9A321;
        }

        .team-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        @media (min-width: 480px) {
          .team-grid {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
        }

        @media (min-width: 768px) {
          .team-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 1.5rem;
          }
        }

        .team-card {
          background: #ffffff;
          border-radius: 12px;
          padding: 1.25rem;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          border: 1px solid rgba(0, 0, 0, 0.04);
        }

        @media (min-width: 768px) {
          .team-card {
            padding: 1.75rem;
          }
        }

        .team-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
          border-color: #D9A321;
        }

        .team-avatar {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 700;
          overflow: hidden;
          font-family: "Montserrat", sans-serif;
        }

        @media (min-width: 768px) {
          .team-avatar {
            width: 72px;
            height: 72px;
            font-size: 1.5rem;
            margin: 0 auto 1rem;
          }
        }

        .team-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .team-card h3 {
          font-size: 0.938rem;
          color: #006B2E;
          margin-bottom: 0.125rem;
          font-family: "Montserrat", sans-serif;
        }

        @media (min-width: 768px) {
          .team-card h3 {
            font-size: 1rem;
            margin-bottom: 0.25rem;
          }
        }

        .team-role {
          font-size: 0.75rem;
          color: #D9A321;
          font-weight: 600;
          margin-bottom: 0.375rem;
        }

        @media (min-width: 768px) {
          .team-role {
            font-size: 0.8rem;
            margin-bottom: 0.5rem;
          }
        }

        .team-card p {
          font-size: 0.813rem;
          color: #5A5A5A;
          line-height: 1.5;
          margin-bottom: 0.5rem;
        }

        @media (min-width: 768px) {
          .team-card p {
            font-size: 0.85rem;
          }
        }

        .team-contact {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.7rem;
          color: #006B2E;
          text-decoration: none;
          margin: 0.125rem 0;
          padding: 0.25rem 0.5rem;
          border-radius: 6px;
          transition: background 0.3s ease;
        }

        @media (min-width: 768px) {
          .team-contact {
            font-size: 0.75rem;
            margin: 0.25rem 0;
          }
        }

        .team-contact:hover {
          background: rgba(0, 107, 46, 0.05);
          text-decoration: underline;
        }

        .team-contact svg {
          font-size: 0.65rem;
          color: #D9A321;
        }

        /* ========== CTA ========== */
        .about-cta {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          padding: 2rem 1.5rem;
          text-align: center;
          color: #ffffff;
        }

        @media (min-width: 768px) {
          .about-cta {
            padding: 4rem 2rem;
          }
        }

        .cta-content {
          max-width: 600px;
          margin: 0 auto;
        }

        .cta-content h2 {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }

        @media (min-width: 768px) {
          .cta-content h2 {
            font-size: 2rem;
            margin-bottom: 0.75rem;
          }
        }

        .cta-content p {
          opacity: 0.9;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
        }

        @media (min-width: 768px) {
          .cta-content p {
            font-size: 1rem;
            margin-bottom: 2rem;
          }
        }

        .cta-buttons {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        @media (min-width: 768px) {
          .cta-buttons {
            gap: 1rem;
          }
        }

        .btn-outline-white {
          border-color: #ffffff;
          color: #ffffff;
          background: transparent;
        }

        .btn-outline-white:hover {
          background: #ffffff;
          color: #006B2E;
        }

        .whatsapp-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 2rem;
          background: #25d366;
          color: #ffffff;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .whatsapp-cta:hover {
          background: #1da851;
          transform: translateY(-2px);
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 576px) {
          .about-hero {
            padding: 2rem 1rem;
          }

          .about-story {
            padding: 2rem 0;
          }

          .about-story__grid {
            gap: 1.5rem;
          }

          .values-grid {
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
          }

          .team-grid {
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
          }

          .team-card {
            padding: 0.875rem;
          }

          .team-avatar {
            width: 48px;
            height: 48px;
            font-size: 1rem;
          }

          .value-card {
            padding: 0.875rem;
          }

          .value-icon-wrapper {
            width: 40px;
            height: 40px;
          }

          .value-icon-wrapper svg {
            font-size: 1rem;
          }

          .about-story__mission {
            padding: 1.25rem;
          }

          .mission-stats {
            gap: 1rem;
          }

          .cta-content h2 {
            font-size: 1.1rem;
          }

          .team-notice {
            font-size: 0.7rem;
            padding: 0.5rem 0.75rem;
          }

          .milestones-grid {
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
          }

          .milestone-card {
            padding: 0.875rem;
          }

          .milestone-year {
            font-size: 1.125rem;
          }

          .about-hero__badges {
            gap: 0.5rem;
            font-size: 0.6rem;
          }
        }

        /* Touch-friendly targets */
        .team-contact,
        .value-card,
        .team-card,
        .milestone-card {
          min-height: 44px;
        }
      `}</style>
    </motion.div>
  );
};

export default About;